/**
 * Client for the feedback control-plane route (#200, FR-16). One thin `submitFeedback`
 * POST helper behind both the post-game rating card and the generic "Share feedback"
 * modal — mirroring `credits-api.ts`/`billing-api.ts`.
 *
 * Identity is OPTIONAL and never client-asserted (D200.3): when a signed guest token is
 * present we include it in the JSON envelope and the SERVER resolves the `playerId` from
 * it; an anonymous submit (no token) is fine. The payload is validated client-side first
 * with the SHARED `validateFeedback` (the same authority the server runs) so obvious
 * mistakes never round-trip. No wire/protocol change — this is plain HTTP (D200.2).
 */

import { validateFeedback, type FeedbackSubmission } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';
import { ApiError } from './api-error';

/** Friendly copy per submit-failure status (FR-9.5). */
const SUBMIT_FAILURE_MESSAGES: Readonly<Record<number, string>> = {
  413: 'That feedback is too long — please shorten it.',
  422: 'Please check your feedback and try again.',
  429: 'You’re sending feedback too quickly — please wait a moment.',
};

/**
 * Submit feedback. `guestToken`, when supplied, lets the server attach the caller's
 * identity (never trusted from the body). Resolves on accept (`202`); throws an
 * {@link ApiError} with a friendly, renderable message on a rejected/failed submit.
 * Throws before any network call if the payload is locally invalid.
 */
export async function submitFeedback(
  submission: FeedbackSubmission,
  guestToken?: string,
): Promise<void> {
  const validation = validateFeedback(submission);
  if (!validation.ok) {
    throw new ApiError(validation.error, 422);
  }
  const res = await fetch(`${SERVER_HTTP_URL}/api/feedback`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      ...submission,
      ...(guestToken ? { guestToken } : {}),
    }),
  });
  if (!res.ok) {
    const message =
      SUBMIT_FAILURE_MESSAGES[res.status] ??
      'Could not send your feedback. Please try again.';
    throw new ApiError(message, res.status);
  }
}
